import { AuthShell } from '@/components/AppShell';
import { FormField } from '@/components/FormField';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { ErrorBanner } from '@/lib/ui';
import { validateReturnTo } from '@adholics/auth';
import { ArrowLeft, KeyRound, Loader2, MailCheck, Sparkles } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

type Stage = 'email' | 'password' | 'magic-sent';

export function LoginPage() {
  const [params] = useSearchParams();
  const errCode = params.get('err');
  const navigate = useNavigate();
  const location = useLocation();
  const { ready, sessionKey, me, signInWithPassword } = useAuth();
  const capturedReturnTo = validateReturnTo((location.state as { from?: unknown } | null)?.from);
  const returnTo = capturedReturnTo ?? '/';

  const [stage, setStage] = useState<Stage>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown | null>(null);

  useEffect(() => {
    if (ready && me?.user) navigate(returnTo, { replace: true });
  }, [ready, me, navigate, returnTo]);

  const onCheckEmail = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const r = await api<{ exists: boolean; hasPassword: boolean }>('/auth/check-email', {
        method: 'POST',
        body: { email: email.trim().toLowerCase() },
      });
      if (!r.exists) {
        setError('user_not_found');
        return;
      }
      if (r.hasPassword) {
        setStage('password');
      } else {
        await sendMagicLink();
      }
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const sendMagicLink = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await api('/auth/login', {
        body: {
          email: email.trim().toLowerCase(),
          ...(capturedReturnTo ? { returnTo: capturedReturnTo } : {}),
        },
      });
      setStage('magic-sent');
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitPassword = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const r = await signInWithPassword(email.trim().toLowerCase(), password);
      if (r.requiresTotp) {
        navigate(`/verify-2fa?ch=${encodeURIComponent(r.challengeId)}`);
        return;
      }
      navigate(returnTo);
    } catch (err) {
      setError(err);
      setPassword('');
    } finally {
      setSubmitting(false);
    }
  };

  if (!ready || (sessionKey && !me)) {
    return (
      <AuthShell>
        <Card>
          <CardContent className="space-y-3 py-10 text-center">
            <Loader2 className="mx-auto size-5 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Signing you in…</p>
          </CardContent>
        </Card>
      </AuthShell>
    );
  }
  if (me?.user) return <Navigate to={returnTo} replace />;

  if (stage === 'magic-sent') {
    return (
      <AuthShell>
        <Card>
          <CardHeader className="space-y-3 text-center">
            <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-brand-soft text-brand">
              <MailCheck className="size-6" aria-hidden />
            </div>
            <h1 className="text-xl font-semibold tracking-tight">Check your email</h1>
            <p className="text-sm text-muted-foreground">
              We sent a sign-in link to <strong className="text-fg">{email}</strong>. It expires in
              15 minutes.
            </p>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {/*
              Body weight, not fine print. This was text-xs muted-foreground —
              the smallest and faintest text on the page — which is not what
              "make sure the text asks them" means. It stays a distinct block
              rather than being folded into the sentence above: that sentence is
              read at second zero while the mail is still expected, and this is
              needed at minute one, when someone is scanning the page for what
              to do next.
            */}
            <p className="rounded-md border border-line bg-surface-2 px-3 py-2 text-sm text-fg">
              Not in your inbox within a minute? Check your junk or spam folder.
            </p>
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => {
                setStage('email');
                setError(null);
              }}
            >
              <ArrowLeft className="size-4" />
              Use a different email
            </Button>
          </CardContent>
        </Card>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      intro={
        <div className="space-y-1.5 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-fg">Welcome back</h1>
          <p className="text-sm text-muted-foreground">
            Sign in to your Adholics workspace to continue.
          </p>
        </div>
      }
    >
      <Card>
        <CardContent className="space-y-4 pt-6">
          <ErrorBanner error={errCode} />

          {stage === 'email' ? (
            <form onSubmit={onCheckEmail} className="space-y-4">
              <FormField label="Work email" required>
                {(id) => (
                  <Input
                    id={id}
                    type="email"
                    inputMode="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    required
                    autoComplete="email"
                    autoFocus
                  />
                )}
              </FormField>
              <ErrorBanner error={error} />
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="size-4 animate-spin" /> Checking…
                  </>
                ) : (
                  'Continue'
                )}
              </Button>
            </form>
          ) : null}

          {stage === 'password' ? (
            <form onSubmit={onSubmitPassword} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Welcome back, <strong className="text-fg">{email}</strong>.
              </p>
              <FormField label="Password" required>
                {(id) => (
                  <Input
                    id={id}
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    autoFocus
                  />
                )}
              </FormField>
              <ErrorBanner error={error} />
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="size-4 animate-spin" /> Signing in…
                  </>
                ) : (
                  <>
                    <KeyRound className="size-4" /> Sign in
                  </>
                )}
              </Button>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  type="button"
                  variant="ghost"
                  className="flex-1"
                  onClick={() => void sendMagicLink()}
                  disabled={submitting}
                >
                  <Sparkles className="size-4" />
                  Send magic link instead
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="flex-1"
                  onClick={() => {
                    setStage('email');
                    setPassword('');
                    setError(null);
                  }}
                >
                  <ArrowLeft className="size-4" />
                  Different email
                </Button>
              </div>
            </form>
          ) : null}
        </CardContent>
      </Card>

      <p className="text-center text-sm text-muted-foreground">
        New to Adholics?{' '}
        <Link to="/signup" className="font-medium text-brand hover:underline">
          Request access
        </Link>
      </p>
    </AuthShell>
  );
}
